// Procedural background embellishments: drifting galaxies, nebulae, and comets
// that fade in and out over long cycles. Deterministic from the run seed; all
// motion derives from world.tick so identical runs render identically.

import * as THREE from 'three';

const MAX_OBJECTS = 16;
const ARENA_SIZE = 200;
const HALF = ARENA_SIZE / 2;

// Healing zones are the green/teal galaxies: hue in this band (green ≈ 0.33,
// teal/cyan ≈ 0.5). See getHealingZones().
const HEALING_HUE_MIN = 0.45;
const HEALING_HUE_MAX = 0.55;

interface BgObject {
  kind: 'galaxy' | 'nebula' | 'comet' | 'blackhole' | 'lucky-star' | 'aegis-moon' | 'swift-comet' | 'gas-cloud';
  x: number;
  y: number;
  vx: number;
  vy: number;
  scale: number;
  rot: number;
  rotSpeed: number;
  hue: number;
  lifeTicks: number;
  startTick: number;
  active: boolean;
  /** Whirlpool dust: tiny motes spiraling into a black hole (see update). */
  swirl?: { angle: number; radius: number; speed: number; size: number }[];
}

/** Motes per black hole: the visible drain. */
const SWIRL_COUNT = 28;

function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function makeGalaxyTexture(): THREE.Texture {
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, 'rgba(255,255,255,0.9)');
  grad.addColorStop(0.25, 'rgba(255,255,255,0.5)');
  grad.addColorStop(0.6, 'rgba(255,255,255,0.12)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  ctx.globalCompositeOperation = 'lighter';
  for (let arm = 0; arm < 3; arm++) {
    ctx.save();
    ctx.translate(size / 2, size / 2);
    ctx.rotate((arm * Math.PI * 2) / 3);
    const armGrad = ctx.createLinearGradient(0, 0, size * 0.45, 0);
    armGrad.addColorStop(0, 'rgba(255,255,255,0.4)');
    armGrad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = armGrad;
    ctx.beginPath();
    ctx.ellipse(size * 0.22, 0, size * 0.22, size * 0.07, 0.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function makeBlackholeTexture(): THREE.Texture {
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  // Accretion ring glow: a soft violet halo peaking outside the horizon.
  const ring = ctx.createRadialGradient(size / 2, size / 2, size * 0.18, size / 2, size / 2, size * 0.5);
  ring.addColorStop(0, 'rgba(80,140,255,0.0)');
  ring.addColorStop(0.45, 'rgba(140,100,255,0.7)');
  ring.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = ring;
  ctx.fillRect(0, 0, size, size);
  // Event horizon: very dark blue-violet (never pure black) with a long,
  // soft alpha falloff so the disc melts into the far background instead of
  // punching a hard-edged hole through the grid.
  const core = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size * 0.26);
  core.addColorStop(0, 'rgba(8,10,24,0.75)');
  core.addColorStop(0.55, 'rgba(8,10,24,0.55)');
  core.addColorStop(1, 'rgba(8,10,24,0)');
  ctx.fillStyle = core;
  ctx.fillRect(0, 0, size, size);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function makeSoftDotTexture(): THREE.Texture {
  const size = 32;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.4, 'rgba(255,255,255,0.6)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function makeGasCloudTexture(): THREE.Texture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const cx = size / 2;
  const cy = size / 2;
  // Multi-color gas cloud: bright core, colorful mid, dark edge.
  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, size / 2);
  grad.addColorStop(0, 'rgba(255,180,220,0.7)');
  grad.addColorStop(0.3, 'rgba(180,120,255,0.45)');
  grad.addColorStop(0.6, 'rgba(100,180,255,0.25)');
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  // Add a few colored wisps for texture. Every wisp stays fully inside the
  // texture — a wisp gradient that reaches the quad edge gets sliced and
  // reads as a hard cut line.
  for (let i = 0; i < 5; i++) {
    const angle = (i / 5) * Math.PI * 2;
    const r = size * (0.1 + i * 0.03); // ≤ 56 px; + radius stays inside 128
    const wx = cx + Math.cos(angle) * r;
    const wy = cy + Math.sin(angle) * r;
    const wisp = ctx.createRadialGradient(wx, wy, 0, wx, wy, size * 0.2);
    const hue = i % 2 === 0 ? 'rgba(255,120,180,0.3)' : 'rgba(120,220,255,0.3)';
    wisp.addColorStop(0, hue);
    wisp.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = wisp;
    ctx.fillRect(0, 0, size, size);
  }
  // Force a soft falloff to the quad edge so no layer can hard-clip.
  ctx.globalCompositeOperation = 'destination-in';
  const mask = ctx.createRadialGradient(cx, cy, 0, cx, cy, size / 2);
  mask.addColorStop(0, 'rgba(255,255,255,1)');
  mask.addColorStop(0.75, 'rgba(255,255,255,1)');
  mask.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = mask;
  ctx.fillRect(0, 0, size, size);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function makeStarTexture(): THREE.Texture {
  const size = 96;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const cx = size / 2;
  const cy = size / 2;
  // Glow.
  const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, size / 2);
  glow.addColorStop(0, 'rgba(255,215,0,0.7)');
  glow.addColorStop(0.5, 'rgba(255,215,0,0.25)');
  glow.addColorStop(1, 'rgba(255,215,0,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, size, size);
  // 5-point star.
  ctx.fillStyle = 'rgba(255,240,180,0.95)';
  ctx.beginPath();
  for (let i = 0; i < 5; i++) {
    const outer = (i * Math.PI * 2) / 5 - Math.PI / 2;
    const inner = outer + Math.PI / 5;
    const rOut = size * 0.28;
    const rIn = size * 0.12;
    ctx.lineTo(cx + Math.cos(outer) * rOut, cy + Math.sin(outer) * rOut);
    ctx.lineTo(cx + Math.cos(inner) * rIn, cy + Math.sin(inner) * rIn);
  }
  ctx.closePath();
  ctx.fill();
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function makeMoonTexture(): THREE.Texture {
  const size = 96;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const cx = size / 2;
  const cy = size / 2;
  // Glow.
  const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, size / 2);
  glow.addColorStop(0, 'rgba(100,180,255,0.6)');
  glow.addColorStop(0.5, 'rgba(100,180,255,0.2)');
  glow.addColorStop(1, 'rgba(100,180,255,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, size, size);
  // Crescent moon.
  ctx.fillStyle = 'rgba(180,220,255,0.9)';
  ctx.beginPath();
  ctx.arc(cx, cy, size * 0.28, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(0,0,0,0.85)';
  ctx.beginPath();
  ctx.arc(cx + size * 0.1, cy - size * 0.05, size * 0.24, 0, Math.PI * 2);
  ctx.fill();
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/** Fade alpha to zero at all four quad edges so no gradient can hard-clip. */
function applyEdgeFade(ctx: CanvasRenderingContext2D, w: number, h: number, margin: number): void {
  ctx.globalCompositeOperation = 'destination-in';
  const gx1 = ctx.createLinearGradient(0, 0, w, 0);
  gx1.addColorStop(0, 'rgba(255,255,255,1)');
  gx1.addColorStop(1 - margin / w, 'rgba(255,255,255,1)');
  gx1.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gx1;
  ctx.fillRect(0, 0, w, h);
  const gx2 = ctx.createLinearGradient(0, 0, w, 0);
  gx2.addColorStop(0, 'rgba(255,255,255,0)');
  gx2.addColorStop(margin / w, 'rgba(255,255,255,1)');
  gx2.addColorStop(1, 'rgba(255,255,255,1)');
  ctx.fillStyle = gx2;
  ctx.fillRect(0, 0, w, h);
  const gy1 = ctx.createLinearGradient(0, 0, 0, h);
  gy1.addColorStop(0, 'rgba(255,255,255,1)');
  gy1.addColorStop(1 - margin / h, 'rgba(255,255,255,1)');
  gy1.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gy1;
  ctx.fillRect(0, 0, w, h);
  const gy2 = ctx.createLinearGradient(0, 0, 0, h);
  gy2.addColorStop(0, 'rgba(255,255,255,0)');
  gy2.addColorStop(margin / h, 'rgba(255,255,255,1)');
  gy2.addColorStop(1, 'rgba(255,255,255,1)');
  ctx.fillStyle = gy2;
  ctx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = 'source-over';
}

function makeSwiftCometTexture(): THREE.Texture {
  const w = 96;
  const h = 32;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  // Green glow (kept inside the texture: center + radius must stay < w).
  const glow = ctx.createRadialGradient(w * 0.75, h / 2, 0, w * 0.75, h / 2, h * 0.7);
  glow.addColorStop(0, 'rgba(100,255,150,0.8)');
  glow.addColorStop(0.5, 'rgba(100,255,150,0.3)');
  glow.addColorStop(1, 'rgba(100,255,150,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, w, h);
  // Tapered tail.
  const tail = ctx.createLinearGradient(0, 0, w * 0.75, 0);
  tail.addColorStop(0, 'rgba(100,255,150,0)');
  tail.addColorStop(0.6, 'rgba(100,255,150,0.4)');
  tail.addColorStop(1, 'rgba(100,255,150,0.9)');
  ctx.fillStyle = tail;
  ctx.beginPath();
  ctx.moveTo(0, h * 0.5);
  ctx.lineTo(w * 0.75, h * 0.15);
  ctx.lineTo(w * 0.75, h * 0.85);
  ctx.closePath();
  ctx.fill();
  // Bright head.
  ctx.fillStyle = 'rgba(220,255,230,0.95)';
  ctx.beginPath();
  ctx.arc(w * 0.75, h / 2, h * 0.3, 0, Math.PI * 2);
  ctx.fill();
  // No hard clips: soft alpha falloff at every quad edge.
  applyEdgeFade(ctx, w, h, 6);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function makeCometTexture(): THREE.Texture {
  const w = 128;
  const h = 32;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  // Soft glow behind the head (kept inside the texture).
  const glow = ctx.createRadialGradient(w * 0.78, h / 2, 0, w * 0.78, h / 2, h * 0.75);
  glow.addColorStop(0, 'rgba(255,255,255,0.8)');
  glow.addColorStop(0.5, 'rgba(255,255,255,0.25)');
  glow.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, w, h);
  // Tapered tail.
  const tail = ctx.createLinearGradient(0, 0, w * 0.78, 0);
  tail.addColorStop(0, 'rgba(255,255,255,0)');
  tail.addColorStop(0.5, 'rgba(255,255,255,0.35)');
  tail.addColorStop(0.9, 'rgba(255,255,255,0.85)');
  tail.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = tail;
  ctx.beginPath();
  ctx.moveTo(0, h * 0.5);
  ctx.lineTo(w * 0.78, h * 0.12);
  ctx.lineTo(w * 0.78, h * 0.88);
  ctx.closePath();
  ctx.fill();
  // Bright head.
  ctx.fillStyle = 'rgba(255,255,255,0.95)';
  ctx.beginPath();
  ctx.arc(w * 0.78, h / 2, h * 0.28, 0, Math.PI * 2);
  ctx.fill();
  // No hard clips: soft alpha falloff at every quad edge.
  applyEdgeFade(ctx, w, h, 6);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

export interface GalaxyZone {
  x: number;
  y: number;
  radius: number;
}

export class BackgroundObjects {
  readonly group: THREE.Group;
  private readonly galaxyMesh: THREE.InstancedMesh;
  private readonly cometMesh: THREE.InstancedMesh;
  private readonly blackholeMesh: THREE.InstancedMesh;
  private readonly swirlMesh: THREE.InstancedMesh;
  private readonly luckyStarMesh: THREE.InstancedMesh;
  private readonly aegisMoonMesh: THREE.InstancedMesh;
  private readonly swiftCometMesh: THREE.InstancedMesh;
  private readonly gasCloudMesh: THREE.InstancedMesh;
  private readonly objects: BgObject[] = [];
  private readonly dummy = new THREE.Object3D();
  private readonly tmpColor = new THREE.Color();
  private readonly galaxyTexture = makeGalaxyTexture();
  private readonly cometTexture = makeCometTexture();
  private readonly blackholeTexture = makeBlackholeTexture();
  private readonly swirlTexture = makeSoftDotTexture();
  private readonly luckyStarTexture = makeStarTexture();
  private readonly aegisMoonTexture = makeMoonTexture();
  private readonly swiftCometTexture = makeSwiftCometTexture();
  private readonly gasCloudTexture = makeGasCloudTexture();
  private seed = 1;

  constructor() {
    this.group = new THREE.Group();
    this.group.renderOrder = -90;

    const geometry = new THREE.PlaneGeometry(1, 1);
    this.galaxyMesh = new THREE.InstancedMesh(
      geometry,
      new THREE.MeshBasicMaterial({
        map: this.galaxyTexture,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
      MAX_OBJECTS
    );
    this.galaxyMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.galaxyMesh.frustumCulled = false;
    this.galaxyMesh.count = 0;
    this.group.add(this.galaxyMesh);

    this.cometMesh = new THREE.InstancedMesh(
      geometry,
      new THREE.MeshBasicMaterial({
        map: this.cometTexture,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
      MAX_OBJECTS
    );
    this.cometMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.cometMesh.frustumCulled = false;
    this.cometMesh.count = 0;
    this.group.add(this.cometMesh);

    this.blackholeMesh = new THREE.InstancedMesh(
      geometry,
      new THREE.MeshBasicMaterial({
        map: this.blackholeTexture,
        transparent: true,
        blending: THREE.NormalBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
      MAX_OBJECTS
    );
    this.blackholeMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.blackholeMesh.frustumCulled = false;
    this.blackholeMesh.count = 0;
    this.group.add(this.blackholeMesh);

    // Whirlpool dust: tiny additive motes spiraling into each black hole.
    this.swirlMesh = new THREE.InstancedMesh(
      geometry,
      new THREE.MeshBasicMaterial({
        map: this.swirlTexture,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
      MAX_OBJECTS * SWIRL_COUNT
    );
    this.swirlMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.swirlMesh.frustumCulled = false;
    this.swirlMesh.count = 0;
    this.group.add(this.swirlMesh);

    const boonMaterial = (map: THREE.Texture) => new THREE.MeshBasicMaterial({
      map,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });

    this.luckyStarMesh = new THREE.InstancedMesh(geometry, boonMaterial(this.luckyStarTexture), MAX_OBJECTS);
    this.luckyStarMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.luckyStarMesh.frustumCulled = false;
    this.luckyStarMesh.count = 0;
    this.group.add(this.luckyStarMesh);

    this.aegisMoonMesh = new THREE.InstancedMesh(geometry, boonMaterial(this.aegisMoonTexture), MAX_OBJECTS);
    this.aegisMoonMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.aegisMoonMesh.frustumCulled = false;
    this.aegisMoonMesh.count = 0;
    this.group.add(this.aegisMoonMesh);

    this.swiftCometMesh = new THREE.InstancedMesh(geometry, boonMaterial(this.swiftCometTexture), MAX_OBJECTS);
    this.swiftCometMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.swiftCometMesh.frustumCulled = false;
    this.swiftCometMesh.count = 0;
    this.group.add(this.swiftCometMesh);

    this.gasCloudMesh = new THREE.InstancedMesh(geometry, boonMaterial(this.gasCloudTexture), MAX_OBJECTS);
    this.gasCloudMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.gasCloudMesh.frustumCulled = false;
    this.gasCloudMesh.count = 0;
    this.group.add(this.gasCloudMesh);

    for (let i = 0; i < MAX_OBJECTS; i++) {
      this.objects.push({
        kind: 'galaxy',
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        scale: 1,
        rot: 0,
        rotSpeed: 0,
        hue: 0,
        lifeTicks: 0,
        startTick: 0,
        active: false,
      });
    }
  }

  setSeed(seed: number): void {
    this.seed = seed >>> 0;
    for (const obj of this.objects) {
      obj.active = false;
    }
  }

  private respawn(obj: BgObject, index: number, tick: number): void {
    const rnd = mulberry32((this.seed + Math.imul(index + 1, 0x9E3779B9) + Math.imul(tick, 0x85EBCA6B)) >>> 0);
    const kindRoll = rnd();
    if (kindRoll < 0.35) obj.kind = 'galaxy';
    else if (kindRoll < 0.62) obj.kind = 'nebula';
    else if (kindRoll < 0.72) obj.kind = 'gas-cloud';
    else if (kindRoll < 0.88) obj.kind = 'comet';
    else if (kindRoll < 0.93) obj.kind = 'blackhole';
    else if (kindRoll < 0.96) obj.kind = 'lucky-star';
    else if (kindRoll < 0.98) obj.kind = 'aegis-moon';
    else obj.kind = 'swift-comet';

    obj.x = (rnd() * 2 - 1) * HALF;
    obj.y = (rnd() * 2 - 1) * HALF;
    const angle = rnd() * Math.PI * 2;
    const speed = obj.kind === 'comet' ? 8 + rnd() * 12 : obj.kind === 'blackhole' ? 0.05 + rnd() * 0.15 : obj.kind === 'swift-comet' ? 4 + rnd() * 6 : 0.2 + rnd() * 0.8;
    obj.vx = Math.cos(angle) * speed;
    obj.vy = Math.sin(angle) * speed;
    obj.scale = obj.kind === 'comet' ? 1.2 + rnd() * 0.8 : obj.kind === 'blackhole' ? 5 + rnd() * 4 : obj.kind === 'nebula' ? 6 + rnd() * 10 : obj.kind === 'gas-cloud' ? 8 + rnd() * 6 : obj.kind === 'lucky-star' || obj.kind === 'aegis-moon' ? 2.5 + rnd() * 1.5 : obj.kind === 'swift-comet' ? 1.5 + rnd() * 1.0 : 3 + rnd() * 6;
    // Comets fly nose-first: the tail always trails the trajectory.
    if (obj.kind === 'comet' || obj.kind === 'swift-comet') {
      obj.rot = Math.atan2(obj.vy, obj.vx);
      obj.rotSpeed = 0;
    } else {
      obj.rot = rnd() * Math.PI * 2;
      // Galaxies and nebulae slowly spin; black holes spin faster.
      obj.rotSpeed = obj.kind === 'blackhole' ? 0.5 + rnd() * 0.5 : 0.04 + rnd() * 0.08;
    }
    // Comet hue sits in blue-violet so the streaks never read as the player's
    // warm-gold projectiles or the cyan swarm. (Visual only; comets are not
    // gameplay zones.)
    obj.hue = obj.kind === 'comet' ? 0.68 : obj.kind === 'blackhole' ? 0.72 : obj.kind === 'gas-cloud' ? rnd() : obj.kind === 'lucky-star' ? 0.12 : obj.kind === 'aegis-moon' ? 0.58 : obj.kind === 'swift-comet' ? 0.38 : 0.5 + rnd() * 0.2;
    obj.lifeTicks = obj.kind === 'comet' ? 180 + rnd() * 120 : obj.kind === 'blackhole' ? 3600 + rnd() * 2400 : 2400 + rnd() * 2400;
    // Whirlpool dust for black holes: motes at staggered radii/angles that
    // will spiral into the vortex (golden-angle respawn keeps the reset
    // deterministic without an RNG at draw time).
    if (obj.kind === 'blackhole') {
      obj.swirl = Array.from({ length: SWIRL_COUNT }, (_, i) => ({
        angle: i * 2.4 + rnd() * 0.6,
        radius: 0.55 + ((i * 7) % SWIRL_COUNT) / SWIRL_COUNT * 1.05,
        speed: 0.9 + rnd() * 0.8,
        size: 0.1 + rnd() * 0.08,
      }));
    } else {
      obj.swirl = undefined;
    }
    obj.startTick = tick;
    obj.active = true;
  }

  /**
   * Active green/teal galaxy zones (world coords) that heal the player.
   * Green/teal = hue in [0.45, 0.55]; if no active galaxy/nebula falls in
   * that band, every active `galaxy` object stands in so a healing zone
   * always exists somewhere in the arena.
   */
  getHealingZones(): GalaxyZone[] {
    const zones: GalaxyZone[] = [];
    for (const obj of this.objects) {
      if (!obj.active || (obj.kind !== 'galaxy' && obj.kind !== 'nebula')) continue;
      if (obj.hue < HEALING_HUE_MIN || obj.hue > HEALING_HUE_MAX) continue;
      zones.push({ x: obj.x, y: obj.y, radius: obj.scale * 0.6 });
    }
    if (zones.length === 0) {
      for (const obj of this.objects) {
        if (!obj.active || obj.kind !== 'galaxy') continue;
        zones.push({ x: obj.x, y: obj.y, radius: obj.scale * 0.6 });
      }
    }
    return zones;
  }

  /** Active galaxy/nebula zones (world coords) that grant a dash-recharge boost. */
  getActiveZones(): GalaxyZone[] {
    const zones: GalaxyZone[] = [];
    for (const obj of this.objects) {
      if (!obj.active || obj.kind === 'comet' || obj.kind === 'blackhole') continue;
      zones.push({ x: obj.x, y: obj.y, radius: obj.scale * 0.6 });
    }
    return zones;
  }

  /** Active black holes: gravity wells that pull entities and swallow flashes.
   * Strength matches the wormhole pull profile (game.ts maps it through
   * wormholePullAccel): thrust escapes outside ~6 u, inside that only a
   * flash breaks free — ambient black holes are real terrain hazards. */
  getGravityWells(): { x: number; y: number; radius: number; strength: number }[] {
    const wells: { x: number; y: number; radius: number; strength: number }[] = [];
    for (const obj of this.objects) {
      if (!obj.active || obj.kind !== 'blackhole') continue;
      wells.push({ x: obj.x, y: obj.y, radius: Math.max(22, obj.scale * 3), strength: 2400 });
    }
    return wells;
  }

  /** Active boon objects with temporary effects: lucky-star, aegis-moon, swift-comet. */
  getBoonZones(): { x: number; y: number; radius: number; kind: 'lucky-star' | 'aegis-moon' | 'swift-comet' }[] {
    const zones: { x: number; y: number; radius: number; kind: 'lucky-star' | 'aegis-moon' | 'swift-comet' }[] = [];
    for (const obj of this.objects) {
      if (!obj.active) continue;
      if (obj.kind === 'lucky-star' || obj.kind === 'aegis-moon' || obj.kind === 'swift-comet') {
        zones.push({ x: obj.x, y: obj.y, radius: obj.scale * 0.8, kind: obj.kind });
      }
    }
    return zones;
  }

  update(tick: number, alpha: number, camera: THREE.OrthographicCamera): void {
    const t = tick + alpha;
    let galaxyCount = 0;
    let cometCount = 0;
    let blackholeCount = 0;
    let luckyStarCount = 0;
    let aegisMoonCount = 0;
    let swiftCometCount = 0;
    let gasCloudCount = 0;
    let swirlCount = 0;
    for (let i = 0; i < this.objects.length; i++) {
      const obj = this.objects[i];
      if (!obj.active) {
        this.respawn(obj, i, tick);
      }
      const age = t - obj.startTick;
      if (age >= obj.lifeTicks) {
        obj.active = false;
        continue;
      }
      obj.x += obj.vx * alpha / 60;
      obj.y += obj.vy * alpha / 60;
      obj.rot += obj.rotSpeed * alpha / 60;
      obj.x = ((obj.x + HALF) % ARENA_SIZE) - HALF;
      obj.y = ((obj.y + HALF) % ARENA_SIZE) - HALF;

      const fade = Math.sin((age / obj.lifeTicks) * Math.PI);
      // Background must sit below the enemy luminance floor: comets, gas
      // clouds and galaxies/nebulae stay dim. Boon objects are gameplay, so
      // they keep a brighter fade; black holes stay dark by nature.
      const isBoon = obj.kind === 'lucky-star' || obj.kind === 'aegis-moon' || obj.kind === 'swift-comet';
      const alphaFade =
        obj.kind === 'comet' ? fade * 0.55
        : obj.kind === 'blackhole' ? fade * 0.7
        : obj.kind === 'gas-cloud' ? fade * 0.28
        : isBoon ? fade * 0.5
        : fade * 0.22;

      const dx = obj.x - camera.position.x;
      const dy = obj.y - camera.position.y;
      const wrapX = dx - Math.round(dx / ARENA_SIZE) * ARENA_SIZE;
      const wrapY = dy - Math.round(dy / ARENA_SIZE) * ARENA_SIZE;

      // Black holes sink deeper into the far background than other décor.
      this.dummy.position.set(
        camera.position.x + wrapX,
        camera.position.y + wrapY,
        obj.kind === 'blackhole' ? -0.55 : -0.4
      );
      this.dummy.rotation.z = obj.rot;
      if (obj.kind === 'comet' || obj.kind === 'swift-comet') {
        this.dummy.scale.set(obj.scale * 2.5, obj.scale * 0.5, 1);
      } else {
        this.dummy.scale.set(obj.scale, obj.scale, 1);
      }
      this.dummy.updateMatrix();

      this.tmpColor.setHSL(obj.hue, 0.7, 0.6);
      this.tmpColor.multiplyScalar(alphaFade);

      if (obj.kind === 'comet') {
        // Draw only every other comet (deterministic parity on respawn tick +
        // slot) so the near-field dash density drops without touching the
        // gameplay-relevant spawn distribution.
        if ((i + obj.startTick) % 2 !== 0) continue;
        this.cometMesh.setMatrixAt(cometCount, this.dummy.matrix);
        this.cometMesh.setColorAt(cometCount, this.tmpColor);
        cometCount++;
      } else if (obj.kind === 'blackhole') {
        this.blackholeMesh.setMatrixAt(blackholeCount, this.dummy.matrix);
        this.blackholeMesh.setColorAt(blackholeCount, this.tmpColor);
        blackholeCount++;
        // Whirlpool dust: each mote tightens its spiral as it drains —
        // angular speed rises inward (conservation-ish), radius falls, and a
        // golden-angle reset recycles swallowed motes at the rim. The reset
        // uses no RNG, so identical runs swirl identically.
        if (obj.swirl) {
          const dt = alpha / 60;
          const rim = obj.scale * 0.55;
          for (const mote of obj.swirl) {
            mote.angle += (mote.speed * 1.5 / Math.max(0.3, mote.radius)) * dt;
            mote.radius -= mote.speed * 0.3 * dt;
            if (mote.radius < 0.3) {
              mote.radius = 1.6;
              mote.angle += 2.4;
            }
            const px = camera.position.x + wrapX + Math.cos(mote.angle) * mote.radius * rim * 0.6;
            const py = camera.position.y + wrapY + Math.sin(mote.angle) * mote.radius * rim * 0.6;
            this.dummy.position.set(px, py, -0.5);
            this.dummy.rotation.z = mote.angle;
            const s = mote.size * (0.7 + 0.6 * (1.6 - mote.radius));
            this.dummy.scale.set(s, s, 1);
            this.dummy.updateMatrix();
            // Brighter as they fall in: the drain reads as accelerating sparks.
            this.tmpColor.setHSL(0.72, 0.85, 0.65 + 0.2 * (1.6 - mote.radius) / 1.3);
            this.tmpColor.multiplyScalar(alphaFade);
            this.swirlMesh.setMatrixAt(swirlCount, this.dummy.matrix);
            this.swirlMesh.setColorAt(swirlCount, this.tmpColor);
            swirlCount++;
          }
        }
      } else if (obj.kind === 'lucky-star') {
        this.luckyStarMesh.setMatrixAt(luckyStarCount, this.dummy.matrix);
        this.luckyStarMesh.setColorAt(luckyStarCount, this.tmpColor);
        luckyStarCount++;
      } else if (obj.kind === 'aegis-moon') {
        this.aegisMoonMesh.setMatrixAt(aegisMoonCount, this.dummy.matrix);
        this.aegisMoonMesh.setColorAt(aegisMoonCount, this.tmpColor);
        aegisMoonCount++;
      } else if (obj.kind === 'swift-comet') {
        this.swiftCometMesh.setMatrixAt(swiftCometCount, this.dummy.matrix);
        this.swiftCometMesh.setColorAt(swiftCometCount, this.tmpColor);
        swiftCometCount++;
      } else if (obj.kind === 'gas-cloud') {
        this.gasCloudMesh.setMatrixAt(gasCloudCount, this.dummy.matrix);
        this.gasCloudMesh.setColorAt(gasCloudCount, this.tmpColor);
        gasCloudCount++;
      } else {
        this.galaxyMesh.setMatrixAt(galaxyCount, this.dummy.matrix);
        this.galaxyMesh.setColorAt(galaxyCount, this.tmpColor);
        galaxyCount++;
      }
    }
    this.galaxyMesh.count = galaxyCount;
    this.cometMesh.count = cometCount;
    this.blackholeMesh.count = blackholeCount;
    this.swirlMesh.count = swirlCount;
    this.luckyStarMesh.count = luckyStarCount;
    this.aegisMoonMesh.count = aegisMoonCount;
    this.swiftCometMesh.count = swiftCometCount;
    this.gasCloudMesh.count = gasCloudCount;
    if (galaxyCount > 0) {
      this.galaxyMesh.instanceMatrix.needsUpdate = true;
      if (this.galaxyMesh.instanceColor) this.galaxyMesh.instanceColor.needsUpdate = true;
    }
    if (cometCount > 0) {
      this.cometMesh.instanceMatrix.needsUpdate = true;
      if (this.cometMesh.instanceColor) this.cometMesh.instanceColor.needsUpdate = true;
    }
    if (blackholeCount > 0) {
      this.blackholeMesh.instanceMatrix.needsUpdate = true;
      if (this.blackholeMesh.instanceColor) this.blackholeMesh.instanceColor.needsUpdate = true;
    }
    if (swirlCount > 0) {
      this.swirlMesh.instanceMatrix.needsUpdate = true;
      if (this.swirlMesh.instanceColor) this.swirlMesh.instanceColor.needsUpdate = true;
    }
    if (luckyStarCount > 0) {
      this.luckyStarMesh.instanceMatrix.needsUpdate = true;
      if (this.luckyStarMesh.instanceColor) this.luckyStarMesh.instanceColor.needsUpdate = true;
    }
    if (aegisMoonCount > 0) {
      this.aegisMoonMesh.instanceMatrix.needsUpdate = true;
      if (this.aegisMoonMesh.instanceColor) this.aegisMoonMesh.instanceColor.needsUpdate = true;
    }
    if (swiftCometCount > 0) {
      this.swiftCometMesh.instanceMatrix.needsUpdate = true;
      if (this.swiftCometMesh.instanceColor) this.swiftCometMesh.instanceColor.needsUpdate = true;
    }
    if (gasCloudCount > 0) {
      this.gasCloudMesh.instanceMatrix.needsUpdate = true;
      if (this.gasCloudMesh.instanceColor) this.gasCloudMesh.instanceColor.needsUpdate = true;
    }
  }

  dispose(): void {
    this.galaxyTexture.dispose();
    this.cometTexture.dispose();
    this.blackholeTexture.dispose();
    this.swirlTexture.dispose();
    this.luckyStarTexture.dispose();
    this.aegisMoonTexture.dispose();
    this.swiftCometTexture.dispose();
    this.gasCloudTexture.dispose();
    this.galaxyMesh.geometry.dispose();
    (this.galaxyMesh.material as THREE.Material).dispose();
    this.cometMesh.geometry.dispose();
    (this.cometMesh.material as THREE.Material).dispose();
    this.blackholeMesh.geometry.dispose();
    (this.blackholeMesh.material as THREE.Material).dispose();
    this.swirlMesh.geometry.dispose();
    (this.swirlMesh.material as THREE.Material).dispose();
    this.luckyStarMesh.geometry.dispose();
    (this.luckyStarMesh.material as THREE.Material).dispose();
    this.aegisMoonMesh.geometry.dispose();
    (this.aegisMoonMesh.material as THREE.Material).dispose();
    this.swiftCometMesh.geometry.dispose();
    (this.swiftCometMesh.material as THREE.Material).dispose();
    this.gasCloudMesh.geometry.dispose();
    (this.gasCloudMesh.material as THREE.Material).dispose();
  }
}
